/**
 * AnalyticsRESTClient
 *
 * Facade coordinadora que:
 *  1. Resuelve el feature flag de analytics UNA SOLA VEZ en el momento del init()
 *  2. Posee la única instancia de NewRelicEventAPI (singleton)
 *  3. Despacha llamadas analíticas de alto nivel a GA4RestClient y NewRelicEventAPI
 *
 * Módulo de cierre (no una clase) — consistente con AnalyticsFeatureFlag.js.
 * El estado interno es compartido entre todas las importaciones del mismo bundle.
 *
 * API pública:
 *   init(config)                — resuelve FF una vez; crea instancia NR si está habilitado
 *   trackEventGA(name, params)     — despacho solo GA4RestClient (evento crudo name+params)
 *   trackEventGA4(event, userId)   — despacho solo GA4RestClient con evento pre-transformado
 *   trackEventNR(name, params)     — despacho solo NewRelicEventAPI
 *   setUserId(id)              — solo NR via setGlobalAttribute
 *   isEnabled()                — retorna el valor cacheado de _enabled
 *   getNRInstance()            — retorna la instancia interna de NR (uso exclusivo NewRelic.ts)
 */

import { isAnalyticsRestEnabled } from './AnalyticsFeatureFlag';
import { GA4RestClient } from './ga-api-rest-ps4';
import NewRelicEventAPI from './NewRelicEventAPI';
import { setAnalyticsClientType, setRestStatus, incrementEventCount } from '../../actions/metrics';
import store from '../../store';

// ---------------------------------------------------------------------------
// Estado interno del módulo
// ---------------------------------------------------------------------------

/** Snapshot del feature flag — resuelto una vez en init() y nunca reevaluado. */
let _enabled = false;

/** Única instancia de NewRelicEventAPI — creada solo cuando el FF está habilitado. */
let _nrInstance = null;

/** Protección contra inicialización doble. */
let _initialized = false;

// ---------------------------------------------------------------------------
// Helpers privados
// ---------------------------------------------------------------------------

// Síncrono — seguro para init() y flush(), que nunca se invocan desde un reducer.
function _dispatch(action) {
    store.dispatch(action);
}

// Diferido — los métodos track* pueden ser invocados desde reducers/metrics.js → setSendMethod.
// Redux lanza "Reducers may not dispatch actions" en despachos síncronos re-entrantes;
// diferir al siguiente tick del event loop evita la invariante sin cambiar el comportamiento
// (los contadores de telemetría no requieren actualizaciones de estado síncronas).
function _dispatchAsync(action) {
    setTimeout(() => store.dispatch(action), 0);
}

// ---------------------------------------------------------------------------
// API pública
// ---------------------------------------------------------------------------

/**
 * Resuelve el feature flag una vez y, si está habilitado, crea el singleton de NR.
 * Las llamadas subsecuentes son no-op (protección idempotente via _initialized).
 *
 * @param {object} [config] - Configuración opcional (reservado para uso futuro).
 */
export function init(config) {
    if (_initialized) return;

    _enabled = isAnalyticsRestEnabled();

    if (_enabled) {
        _nrInstance = new NewRelicEventAPI();
        _dispatch(setRestStatus('activo'));
    }

    _dispatch(setAnalyticsClientType(_enabled ? 'rest' : 'sdk'));

    _initialized = true;
}

/**
 * Despacha un evento nombrado solo a GA4RestClient.
 * Usar desde analytics.js y reducers/metrics.js — ensambla el evento en el facade.
 *
 * @param {string} eventName
 * @param {object} params
 */
export function trackEventGA(eventName, params) {
    if (!_enabled) return;

    _dispatchAsync(incrementEventCount());

    GA4RestClient.send({
        name: eventName,
        params: { ...params, engagement_time_msec: 100 },
    });
}

/**
 * Despacha un evento pre-transformado a GA4RestClient y establece el user_id.
 * Usar desde GA4.ts después de aplicar DataLayerTransformer — el evento llega
 * con el schema correcto y no debe ser re-envuelto.
 *
 * @param {{ name: string, params: object }} event   Objeto de evento GA4 MP.
 * @param {string|undefined}                userId   user_id del payload transformado.
 */
export function trackEventGA4(event, userId) {
    if (!_enabled) return;

    _dispatchAsync(incrementEventCount());

    GA4RestClient.setUserId(userId);
    GA4RestClient.send(event);
}

/**
 * Despacha un evento nombrado solo a NewRelicEventAPI.
 * Usar desde NewRelic.ts para evitar el doble envío a GA4.
 *
 * @param {string} eventName
 * @param {object} params
 */
export function trackEventNR(eventName, params) {
    if (!_enabled) return;

    _dispatchAsync(incrementEventCount());

    try {
        _nrInstance.sendEvent(eventName, { ...params });
    } catch (e) {
        console.error('[AnalyticsRESTClient] trackEventNR: NR sendEvent threw', e);
    }
}

/**
 * Establece un identificador de usuario persistente en NewRelic.
 * No-op en GA4 (userId es a nivel de payload, no de cliente).
 * No-op si la facade no está habilitada.
 *
 * @param {string} id - Identificador de usuario.
 */
export function setUserId(id) {
    if (!_enabled) return;

    _nrInstance.setGlobalAttribute('userId', id);
}

/**
 * Retorna el valor cacheado del feature flag.
 *
 * @returns {boolean}
 */
export function isEnabled() {
    return _enabled;
}

/**
 * Retorna la instancia interna de NewRelicEventAPI, o null cuando el FF está deshabilitado.
 *
 * Destinado para que NewRelic.ts llame directamente a setGlobalAttribute para atributos
 * específicos de NR (appVersion, currentRoute) que no tienen contraparte en GA4 y no
 * forman parte de la API de despacho estándar.
 *
 * @returns {import('./NewRelicEventAPI').default | null}
 */
export function getNRInstance() {
    return _nrInstance;
}



// WEBPACK FOOTER //
// ./src/utils/trackers/AnalyticsRESTClient.js